import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWriteEnableStore } from '@/stores/writeEnableStore';
import {
  ArrowLeft,
  Shield,
  Users,
  Video,
  Phone,
  Building2,
  Grid3X3,
  Lock,
  Minus,
  ChevronDown,
  ChevronUp,
  Clock,
  Download,
  RefreshCw,
  BarChart3,
  Radio,
  ScrollText,
  PhoneCall,
  Monitor,
  Zap,
} from 'lucide-react';
import type {
  ZoomOverviewData,
  NotAvailable as NotAvailableType,
  SettingsData,
  UsersData,
  PhoneData,
  RoomsData,
  PlansData,
  RolesData,
  GroupsData,
  MarketplaceData,
  ArchiveData,
  MeetingQualityData,
  SatisfactionData,
  PhoneCallLogsData,
  LiveMeetingsData,
  OperationLogsData,
  TelephoneData,
  RoomMetricsData,
} from './overview-types';
import { isNotAvailable } from './overview-types';
import { useOverviewStore } from '@/stores/overviewStore';
import { usePlansStore } from '@/stores/plansStore';
import { client } from '@/lib/api';
import type { plans } from '@/lib/client';
import { AccountActivity } from './AccountActivity';

import { UsageChart } from './UsageChart';
import { TopMeetings } from './TopMeetings';
import { generateOverviewPdf } from './OverviewPdfExport';
import { MeetingQualitySection } from './MeetingQualitySection';
import { PhoneCallLogsSection } from './PhoneCallLogsSection';
import { LiveMeetingsSection } from './LiveMeetingsSection';
import { OperationLogsSection } from './OperationLogsSection';

// =============================================================================
// Helpers
// =============================================================================

function formatPlanName(raw: string): string {
  const PLAN_NAMES: Record<string, string> = {
    business_yearly: 'Business (Yearly)',
    business_monthly: 'Business (Monthly)',
    pro_yearly: 'Pro (Yearly)',
    pro_monthly: 'Pro (Monthly)',
    enterprise_yearly: 'Enterprise (Yearly)',
    enterprise_monthly: 'Enterprise (Monthly)',
    basic: 'Basic',
    education: 'Education',
    zoom_one_business: 'Zoom One Business',
    zoom_one_enterprise: 'Zoom One Enterprise',
    zoom_one_pro: 'Zoom One Pro',
  };
  return PLAN_NAMES[raw.toLowerCase()] || raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// =============================================================================
// Main component
// =============================================================================

interface ZoomOverviewProps {
  data: ZoomOverviewData | Partial<ZoomOverviewData>;
  /** Absent in single-account mode — the back button is hidden. */
  onBack?: () => void;
  onRefresh: () => void;
  isStreaming?: boolean;
  accountId?: string;
}

export function ZoomOverview({ data, onBack, onRefresh, isStreaming, accountId }: ZoomOverviewProps) {
  const navigate = useNavigate();
  const quickStatsReady = !!(data.users && data.usage && data.recordings && data.phone);
  const usageReady = !!data.usage && !isStreaming; // enrichment completes at end of streaming

  // Background regeneration state + trigger (after a successful fix the
  // report regenerates silently; the activity feed refetches).
  const isRefreshing = useOverviewStore((s) => s.isRefreshing);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);

  // Per-action Fix button state. Keyed by fix action id (not list index)
  // so a re-streamed report with the same suggestion keeps its state.
  const [fixStates, setFixStates] = useState<Record<string, 'pending' | 'done' | 'failed' | 'confirm'>>({});
  const [fixMessages, setFixMessages] = useState<Record<string, string>>({});

  // A fix is a plan (docs/plans/change-planner.md D10): drafted from the suggestion, approved and run in
  // one go — the click is the consent — then followed to its end; its line is the message shown. A plan
  // blocked by write access (or scopes the app lacks) is kept, and the setup chat enables it, then opens it.
  const [fixPlans, setFixPlans] = useState<Record<string, string>>({});
  const runPlan = async (key: string, draft: () => Promise<plans.CreatePlanResponse>) => {
    setFixStates((prev) => ({ ...prev, [key]: 'pending' }));
    const fail = (message: string) => {
      setFixStates((prev) => ({ ...prev, [key]: 'failed' }));
      setFixMessages((prev) => ({ ...prev, [key]: message }));
    };
    try {
      const reply = await draft();
      if (reply.status === 'needs_confirm') {
        setFixStates((prev) => ({ ...prev, [key]: 'confirm' }));
        setFixMessages((prev) => ({ ...prev, [key]: reply.message ?? 'Confirm to plan it.' }));
        return;
      }
      if (reply.status !== 'plan' || !reply.plan) {
        fail(reply.message ?? "I can't automate that one — change it by hand in Zoom.");
        return;
      }
      const plan = reply.plan;
      setFixPlans((prev) => ({ ...prev, [key]: plan.id }));
      if (!plan.readiness.ok) {
        if (plan.readiness.blockers.some((b) => b.code === 'write_access') || plan.missing_scopes.length > 0) {
          setFixStates((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
          useWriteEnableStore.getState().setPendingPlan({ planId: plan.id, ...(plan.missing_scopes.length > 0 ? { scopes: plan.missing_scopes } : {}) });
          navigate('/chat?setup=write');
          return;
        }
        fail(plan.readiness.blockers[0]?.detail ?? 'the plan is not ready');
        return;
      }
      let line = '';
      await usePlansStore.getState().approveAndRunFromChat(plan.id, false, (said) => { line = said; });
      const done = usePlansStore.getState().plansById[plan.id]?.status === 'completed';
      setFixStates((prev) => ({ ...prev, [key]: done ? 'done' : 'failed' }));
      setFixMessages((prev) => ({ ...prev, [key]: line }));
      // The plan changed the account (or tried): refresh the activity feed now, and on success
      // regenerate the report in the background.
      setActivityRefreshKey((k) => k + 1);
      if (done && accountId) void useOverviewStore.getState().silentRefresh(accountId);
    } catch (err) {
      fail(err instanceof Error ? err.message : 'Something went wrong — try again.');
    }
  };
  const runFix = (action: string) => runPlan(action, () => client.plans.createPlan({ source: { kind: 'fix', action } }));
  // An actionable suggestion with no curated action: the backend infers the setting; `confirm`
  // accepts a low-confidence mapping.
  const runInferFix = (key: string, text: string, hint: string | null | undefined, confirm: boolean) =>
    runPlan(key, () => client.plans.createPlan({ source: { kind: 'inferred_fix', text, hint: hint ?? undefined, confirm } }));



  return (
    <div className="mx-auto flex max-w-7xl items-start gap-6 px-4 sm:px-6 py-6">
      {/* Account activity — sticky rail left of the report (xl+); on smaller
          screens it renders below the report instead. Always visible. */}
      <aside className="hidden xl:block w-72 shrink-0">
        <div className="sticky top-6">
          <AccountActivity refreshKey={activityRefreshKey} accountId={accountId} />
        </div>
      </aside>

      <div className="min-w-0 max-w-5xl flex-1 space-y-6">
      {data.account && (
        <OverviewHeader data={data as ZoomOverviewData} onBack={onBack} onRefresh={onRefresh} isStreaming={isStreaming} isRefreshing={isRefreshing} />
      )}

      {/* ZAC Insights */}
      {(data.aiSummary?.overallEvaluation || isStreaming) && (
        <StreamingBlur loading={isStreaming && !data.aiSummary}>
          <div className="card overflow-hidden">
            <div className="flex items-center gap-3 border-b border-border px-5 py-4">
              <Zap className="h-5 w-5 text-primary" />
              <h3 className="flex-1 text-sm font-semibold text-foreground">ZAC Insights</h3>
            </div>
            <div className="p-5">
              <div className="space-y-5">
                {/* Lighthouse-style score gauges */}
                <div className="flex items-end justify-center gap-4 sm:gap-6 md:gap-8 min-w-[340px]">
                  <ScoreGauge score={data.aiSummary?.scores.security ?? 0} label="Security" category="security" />
                  <ScoreGauge score={data.aiSummary?.scores.adoption ?? 0} label="Adoption" category="adoption" />
                  <ScoreGauge score={data.aiSummary?.scores.overall ?? 0} label="Overall" size="lg" />
                  <ScoreGauge score={data.aiSummary?.scores.usage ?? 0} label="Usage" category="usage" />
                  <ScoreGauge score={data.aiSummary?.scores.configuration ?? 0} label="Config" category="configuration" />
                </div>

                {(data.aiSummary?.keyPoints.length ?? 0) > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Key Points</p>
                    <ul className="space-y-1.5">
                      {data.aiSummary!.keyPoints.map((point, i) => (
                        <li key={i} className="flex gap-2 text-sm text-foreground/80">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(data.aiSummary?.suggestions.length ?? 0) > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Suggestions</p>
                    <ul className="space-y-1.5">
                      {data.aiSummary!.suggestions.map((suggestion, i) => {
                        const isObj = typeof suggestion !== 'string';
                        const text = isObj ? suggestion.text : suggestion;
                        const action = isObj ? suggestion.action ?? null : null;
                        const hint = isObj ? suggestion.settingHint ?? null : null;
                        // A Fix button shows for known actions and for inferred
                        // (actionable, no action). Advisory suggestions get none.
                        const inferable = isObj && !action && suggestion.actionable === true;
                        const fixable = !!action || inferable;
                        const key = action ?? `infer:${i}`;
                        const state = fixable ? fixStates[key] : undefined;
                        const trigger = () =>
                          action ? runFix(action) : runInferFix(key, text, hint, state === 'confirm');
                        return (
                          <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-500" />
                            <span className="flex-1">
                              {text}
                              {fixable && (state === 'failed' || state === 'confirm') && fixMessages[key] && (
                                <span className={`mt-0.5 block text-xs ${state === 'confirm' ? 'text-amber-400' : 'text-red-400'}`}>{fixMessages[key]}</span>
                              )}
                              {fixable && fixPlans[key] && (
                                <Link to={`/changes/${fixPlans[key]}`} className="mt-0.5 block text-xs text-primary hover:underline">Open the plan</Link>
                              )}
                            </span>
                            {fixable && state === 'done' && (
                              <span className="shrink-0 text-xs font-medium text-green-500">✓ Fixed</span>
                            )}
                            {fixable && state !== 'done' && (
                              <button
                                onClick={trigger}
                                disabled={state === 'pending'}
                                className="shrink-0 rounded-md border border-border bg-secondary px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-secondary/70 disabled:cursor-wait disabled:opacity-60"
                              >
                                {state === 'pending' ? 'Fixing…'
                                  : state === 'confirm' ? 'Confirm fix'
                                  : state === 'failed' ? 'Retry fix'
                                  : 'Fix'}
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {data.aiSummary?.overallEvaluation && (
                  <div className="border-t border-border pt-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Overall Evaluation</p>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {data.aiSummary.overallEvaluation}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </StreamingBlur>
      )}

      {/* Live meetings indicator (near top for real-time visibility) */}
      {data.liveMeetings && (
        <div className="animate-fade-in">
          {isNotAvailable(data.liveMeetings) ? (
            <SectionCard title="Live Meetings" icon={<Radio className="h-5 w-5" />}>
              <NotAvailableBanner message={(data.liveMeetings as NotAvailableType).message} />
            </SectionCard>
          ) : (
            <LiveMeetingsSection data={data.liveMeetings as LiveMeetingsData} accountId={accountId} />
          )}
        </div>
      )}

      {data.account && (
        <StreamingBlur loading={isStreaming && !quickStatsReady}>
          <QuickStats data={data as ZoomOverviewData} />
        </StreamingBlur>
      )}

      {data.usage && (
        <StreamingBlur loading={isStreaming && !usageReady}>
          <div id="pdf-usage-chart" className="animate-fade-in">
            <UsageChart usage={data.usage} />
          </div>
        </StreamingBlur>
      )}

      {data.usage && (
        <div id="pdf-top-meetings" className="animate-fade-in">
          <TopMeetings usage={data.usage} />
        </div>
      )}

      {/* Meeting Quality + Client Satisfaction (2-col grid) */}
      {(data.meetingQuality || data.satisfaction) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
          {data.meetingQuality && (
            isNotAvailable(data.meetingQuality) ? (
              <SectionCard title="Meeting Quality" icon={<BarChart3 className="h-5 w-5" />}>
                <NotAvailableBanner message={(data.meetingQuality as NotAvailableType).message} />
              </SectionCard>
            ) : (
              <MeetingQualitySection data={data.meetingQuality as MeetingQualityData} />
            )
          )}
          {data.satisfaction && (
            isNotAvailable(data.satisfaction) ? (
              <SectionCard title="Client Satisfaction" icon={<span className="text-lg">&#9733;</span>}>
                <NotAvailableBanner message={(data.satisfaction as NotAvailableType).message} />
              </SectionCard>
            ) : (
              <SatisfactionCard data={data.satisfaction as SatisfactionData} />
            )
          )}
        </div>
      )}

      {data.settings && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
          <SecuritySection settings={data.settings} />
          <MeetingSection settings={data.settings} />
        </div>
      )}

      {data.users && (
        <div className="animate-fade-in">
          <UsersSection users={data.users} />
        </div>
      )}

      {(data.phone || data.rooms) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
          {data.phone && <PhoneSection phone={data.phone} />}
          {data.rooms && <RoomsSection rooms={data.rooms} />}
        </div>
      )}

      {/* Phone Call Logs (below phone settings) */}
      {data.phoneCallLogs && (
        <div className="animate-fade-in">
          {isNotAvailable(data.phoneCallLogs) ? (
            <SectionCard title="Phone Call Logs" icon={<Phone className="h-5 w-5" />}>
              <NotAvailableBanner message={(data.phoneCallLogs as NotAvailableType).message} />
            </SectionCard>
          ) : (
            <PhoneCallLogsSection data={data.phoneCallLogs as PhoneCallLogsData} />
          )}
        </div>
      )}

      {/* Telephone Report + Room Metrics (2-col grid) */}
      {(data.telephone || data.roomMetrics) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
          {data.telephone && (
            isNotAvailable(data.telephone) ? (
              <SectionCard title="Telephone Usage" icon={<PhoneCall className="h-5 w-5" />}>
                <NotAvailableBanner message={(data.telephone as NotAvailableType).message} />
              </SectionCard>
            ) : (
              <TelephoneCard data={data.telephone as TelephoneData} />
            )
          )}
          {data.roomMetrics && (
            isNotAvailable(data.roomMetrics) ? (
              <SectionCard title="Room Metrics" icon={<Monitor className="h-5 w-5" />}>
                <NotAvailableBanner message={(data.roomMetrics as NotAvailableType).message} />
              </SectionCard>
            ) : (
              <RoomMetricsCard data={data.roomMetrics as RoomMetricsData} />
            )
          )}
        </div>
      )}

      {(data.roles || data.groups || data.marketplace || data.archive) && (
        <div className="animate-fade-in">
          <IntegrationsSection data={data as ZoomOverviewData} />
        </div>
      )}

      {/* Operation Logs (collapsible, default closed, at the bottom) */}
      {data.operationLogs && (
        <div className="animate-fade-in">
          {isNotAvailable(data.operationLogs) ? (
            <SectionCard title="Operation Logs" icon={<ScrollText className="h-5 w-5" />}>
              <NotAvailableBanner message={(data.operationLogs as NotAvailableType).message} />
            </SectionCard>
          ) : (
            <OperationLogsSection data={data.operationLogs as OperationLogsData} />
          )}
        </div>
      )}

      {/* Account activity fallback below the report on screens without the rail. */}
      <div className="xl:hidden">
        <AccountActivity refreshKey={activityRefreshKey} accountId={accountId} />
      </div>
      </div>
    </div>
  );
}

// =============================================================================
// Streaming blur overlay
// =============================================================================

function StreamingBlur({ children, loading }: { children: React.ReactNode; loading?: boolean }) {
  return (
    <div className="relative">
      <div className={`transition-all duration-500 ease-out ${loading ? 'blur-[2px] opacity-60 select-none pointer-events-none' : 'blur-0 opacity-100'}`}>
        {children}
      </div>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="rounded-full bg-card/90 border border-border/50 px-3 py-1 text-xs text-muted-foreground backdrop-blur-sm shadow-sm">
            Still loading...
          </span>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Score Gauges (Lighthouse-style)
// =============================================================================

const CATEGORY_COLORS: Record<string, { ring: string; track: string }> = {
  security:      { ring: 'text-amber-500',   track: 'text-amber-500/15' },
  adoption:      { ring: 'text-orange-400',  track: 'text-orange-400/15' },
  usage:         { ring: 'text-yellow-500',  track: 'text-yellow-500/15' },
  configuration: { ring: 'text-orange-600',  track: 'text-orange-600/15' },
};

function overallColor(score: number) {
  if (score >= 80) return { ring: 'text-green-500', track: 'text-green-500/15' };
  if (score >= 50) return { ring: 'text-yellow-500', track: 'text-yellow-500/15' };
  return { ring: 'text-red-500', track: 'text-red-500/15' };
}

function ScoreGauge({ score, label, size = 'sm', category }: { score: number; label: string; size?: 'sm' | 'lg'; category?: string }) {
  const isLg = size === 'lg';
  const svgSize = isLg ? 72 : 48;
  const radius = isLg ? 30 : 19;
  const stroke = isLg ? 4 : 3;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const { ring, track } = isLg ? overallColor(score) : (category && CATEGORY_COLORS[category]) || overallColor(score);
  const center = svgSize / 2;

  return (
    <div className="flex flex-col items-center gap-1 shrink-0">
      <div className={`relative ${isLg ? 'h-[72px] w-[72px]' : 'h-12 w-12'}`}>
        <svg className={`${isLg ? 'h-[72px] w-[72px]' : 'h-12 w-12'} -rotate-90`} viewBox={`0 0 ${svgSize} ${svgSize}`}>
          <circle cx={center} cy={center} r={radius} fill="none" strokeWidth={stroke} className={`stroke-current ${track}`} />
          <circle
            cx={center} cy={center} r={radius} fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className={`stroke-current ${ring} transition-all duration-700 ease-out`}
          />
        </svg>
        <span className={`absolute inset-0 flex items-center justify-center font-bold ${ring} ${isLg ? 'text-xl' : 'text-xs'}`}>
          {score}
        </span>
      </div>
      <span className={`text-center font-medium text-muted-foreground ${isLg ? 'text-xs' : 'text-[10px]'}`}>{label}</span>
    </div>
  );
}

// =============================================================================
// Header
// =============================================================================

function OverviewHeader({ data, onBack, onRefresh, isStreaming, isRefreshing }: { data: ZoomOverviewData | Partial<ZoomOverviewData>; onBack?: () => void; onRefresh: () => void; isStreaming?: boolean; isRefreshing?: boolean }) {
  const [exporting, setExporting] = useState(false);

  const rawPlan = !data.plans || isNotAvailable(data.plans)
    ? null
    : (data.plans as PlansData).planBase?.type || null;
  const planLabel = rawPlan ? formatPlanName(rawPlan) : null;

  async function handleExport() {
    setExporting(true);
    try {
      await generateOverviewPdf(data as ZoomOverviewData);
    } finally {
      setExporting(false);
    }
  }

  const account = data.account;
  const avatarName = account?.ownerName || account?.name || '';
  const words = avatarName.trim().split(/\s+/);
  const initials = words.length >= 2
    ? (words[0]![0]! + words[1]![0]!).toUpperCase()
    : avatarName.charAt(0).toUpperCase() || '?';

  return (
    <div className="space-y-3">
      {/* Top row: back button (multi-account only) + actions */}
      <div className="flex items-center justify-between">
        {onBack ? (
          <button
            onClick={onBack}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2 sm:gap-3">
          {isRefreshing && (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <RefreshCw className="h-3 w-3 animate-spin" />
              Updating…
            </span>
          )}
          {data.fetchedAt && (
            <span className="hidden sm:inline text-xs text-muted-foreground">
              <Clock className="mr-1 inline h-3 w-3" />
              {new Date(data.fetchedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button onClick={handleExport} disabled={exporting || isStreaming} className="btn-secondary text-xs">
            <Download className="mr-1 sm:mr-1.5 inline h-3.5 w-3.5" />
            <span className="hidden sm:inline">{exporting ? 'Exporting...' : 'Download PDF'}</span>
            <span className="sm:hidden">{exporting ? '...' : 'PDF'}</span>
          </button>
          <button onClick={onRefresh} disabled={isStreaming} className="btn-secondary text-xs">
            <RefreshCw className="h-3.5 w-3.5 sm:hidden" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Account info row */}
      <div className="flex items-center gap-3 sm:gap-4">
        {account?.ownerPicUrl ? (
          <img
            src={account.ownerPicUrl}
            alt={avatarName}
            className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover ring-2 ring-border shrink-0"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden'); }}
          />
        ) : null}
        <div className={`flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-primary ring-2 ring-primary/30 shrink-0 ${account?.ownerPicUrl ? 'hidden' : ''}`}>
          <span className="text-sm font-semibold text-primary-foreground">{initials}</span>
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2.5">
            <h1 className="text-lg sm:text-xl font-bold text-foreground truncate">{account?.name}</h1>
            {planLabel && <Badge variant="blue">{planLabel}</Badge>}
            {data.scope && (
              <Badge variant={data.scope === 'admin' ? 'green' : 'default'}>
                {data.scope === 'admin' ? 'Admin' : 'User-level'}
              </Badge>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-muted-foreground">
            {account?.ownerName && <span>{account.ownerName}</span>}
            {account?.ownerName && account?.email && <span className="opacity-40">·</span>}
            {account?.email && <span className="truncate">{account.email}</span>}
            {account?.id && (
              <>
                <span className="hidden sm:inline opacity-40">·</span>
                <span className="hidden sm:inline text-xs opacity-60">ID: {account.id}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Quick Stats
// =============================================================================

function QuickStats({ data }: { data: ZoomOverviewData | Partial<ZoomOverviewData> }) {
  const phoneUsers = data.phone ? (data.phone._status === 'provisioned' ? (data.phone.totalUsers ?? 0) : '—') : '—';
  const meetings = !data.usage ? '—' : isNotAvailable(data.usage) ? '—' : (data.usage as any).totalMeetings;
  const usageScope = !data.usage ? undefined : isNotAvailable(data.usage) ? undefined : (data.usage as any)._scope as string | undefined;
  const recScope = data.recordings?._scope;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      <MetricCard label="Total Users" value={data.users?.totalRecords ?? '—'} scope={data.users?._scope} />
      <MetricCard label="Active Users" value={data.users?.byStatus['active'] ?? data.users?.totalRecords ?? '—'} scope={data.users?._scope} />
      <MetricCard label="Meetings (Month)" value={meetings} scope={usageScope} subtitle={data.usage && isNotAvailable(data.usage) ? 'Requires admin' : undefined} />
      <MetricCard label="Cloud Recordings" value={data.recordings?.totalRecords ?? '—'} scope={recScope} />
      <MetricCard label="Phone Users" value={phoneUsers} subtitle={data.phone?._status === 'not_provisioned' ? 'Not provisioned' : undefined} />
    </div>
  );
}

function MetricCard({ label, value, subtitle, scope }: { label: string; value: string | number; subtitle?: string; scope?: string }) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        {scope && (
          <span className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 ${scope === 'admin' ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'}`}>
            {scope === 'admin' ? 'org' : 'you'}
          </span>
        )}
      </div>
      <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
      {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

// =============================================================================
// Section Card (reusable wrapper)
// =============================================================================

function SectionCard({
  title,
  icon,
  badge,
  children,
  collapsible = false,
  defaultOpen = true,
}: {
  title: string;
  icon: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="card overflow-hidden">
      <div
        className={`flex items-center gap-3 border-b border-border px-5 py-4 ${collapsible ? 'cursor-pointer hover:bg-secondary/50' : ''}`}
        onClick={collapsible ? () => setOpen(!open) : undefined}
      >
        <span className="text-muted-foreground">{icon}</span>
        <h3 className="flex-1 text-sm font-semibold text-foreground">{title}</h3>
        {badge}
        {collapsible && (open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />)}
      </div>
      {(!collapsible || open) && <div className="p-5">{children}</div>}
    </div>
  );
}

// =============================================================================
// Badges & Banners
// =============================================================================

function Badge({ children, variant = 'default' }: { children: React.ReactNode; variant?: 'default' | 'green' | 'red' | 'blue' | 'yellow' }) {
  const styles = {
    default: 'bg-secondary text-muted-foreground',
    green: 'bg-green-500/10 text-green-500',
    red: 'bg-red-500/10 text-red-500',
    blue: 'bg-blue-500/10 text-blue-400',
    yellow: 'bg-yellow-500/10 text-yellow-500',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[variant]}`}>
      {children}
    </span>
  );
}

function StatusBadge({ enabled }: { enabled?: boolean }) {
  if (enabled === undefined || enabled === null) return <span className="text-xs text-muted-foreground">—</span>;
  return enabled
    ? <Badge variant="green">Enabled</Badge>
    : <Badge variant="default">Disabled</Badge>;
}

function NotAvailableBanner({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-yellow-500/20 bg-yellow-500/5 px-4 py-3">
      <Lock className="h-4 w-4 shrink-0 text-yellow-500" />
      <p className="text-sm text-yellow-400/80">{message}</p>
    </div>
  );
}

function NotProvisionedBanner({ product }: { product: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-secondary/30 px-4 py-3">
      <Minus className="h-4 w-4 shrink-0 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">{product} is not provisioned for this account</p>
    </div>
  );
}

// =============================================================================
// Setting Row
// =============================================================================

function SettingRow({ label, value }: { label: string; value?: boolean | string | number }) {
  if (value === undefined || value === null) return null;
  return (
    <div className="flex items-center justify-between border-b border-border/50 py-2 last:border-0">
      <span className="text-sm text-foreground/80">{label}</span>
      {typeof value === 'boolean' ? <StatusBadge enabled={value} /> : <span className="text-sm text-muted-foreground">{String(value)}</span>}
    </div>
  );
}

// =============================================================================
// Security Section
// =============================================================================

function SecuritySection({ settings }: { settings: SettingsData }) {
  const { security } = settings;
  return (
    <SectionCard title="Security" icon={<Shield className="h-5 w-5" />} badge={<Badge variant={settings._scope === 'admin' ? 'green' : 'default'}>{settings._scope}</Badge>}>
      <SettingRow label="Waiting Room" value={security.waitingRoom} />
      <SettingRow label="Require Meeting Password" value={security.requirePassword} />
      <SettingRow label="Embed Password in Link" value={security.embedPasswordInLink} />
      <SettingRow label="End-to-End Encryption" value={security.e2eEncryption} />
      <SettingRow label="Two-Factor Auth" value={security.signedLogin} />
      {security.approvedDomains && security.approvedDomains.length > 0 && (
        <div className="flex items-start justify-between border-b border-border/50 py-2 last:border-0">
          <span className="text-sm text-foreground/80">Approved Domains</span>
          <div className="flex flex-wrap gap-1 justify-end">
            {security.approvedDomains.map((d, i) => (
              <span key={i} className="rounded bg-secondary px-1.5 py-0.5 text-xs text-muted-foreground">{d}</span>
            ))}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// =============================================================================
// Meeting & Recording Section
// =============================================================================

function MeetingSection({ settings }: { settings: SettingsData }) {
  const { meeting, recording } = settings;
  return (
    <SectionCard title="Meeting & Recording" icon={<Video className="h-5 w-5" />}>
      <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Meeting</p>
          <SettingRow label="Host Video" value={meeting.hostVideo} />
          <SettingRow label="Participant Video" value={meeting.participantVideo} />
          <SettingRow label="Join Before Host" value={meeting.joinBeforeHost} />
          <SettingRow label="Mute Upon Entry" value={meeting.muteUponEntry} />
          <SettingRow label="Screen Sharing" value={meeting.screenSharing} />
          <SettingRow label="Breakout Rooms" value={meeting.breakoutRoom} />
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recording</p>
          <SettingRow label="Auto Recording" value={meeting.autoRecording} />
          <SettingRow label="Local Recording" value={recording.localRecording} />
          <SettingRow label="Cloud Recording" value={recording.cloudRecording} />
          <SettingRow label="Auto Delete" value={recording.autoDelete} />
          {recording.autoDeleteDays && <SettingRow label="Delete After" value={`${recording.autoDeleteDays} days`} />}
        </div>
      </div>
    </SectionCard>
  );
}

// =============================================================================
// Users Section
// =============================================================================

function UsersSection({ users }: { users: UsersData }) {
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll ? users.users : users.users.slice(0, 10);

  return (
    <SectionCard
      title="Users"
      icon={<Users className="h-5 w-5" />}
      badge={<Badge variant="blue">{users.totalRecords} total</Badge>}
    >
      {/* Summary chips */}
      <div className="mb-4 flex flex-wrap gap-3">
        {Object.entries(users.byType).map(([type, count]) => (
          <span key={type} className="text-sm text-muted-foreground">
            {type}: <span className="font-medium text-foreground">{count}</span>
          </span>
        ))}
        {Object.entries(users.byStatus).map(([status, count]) => (
          <span key={status} className="text-sm capitalize text-muted-foreground">
            {status}: <span className="font-medium text-foreground">{count}</span>
          </span>
        ))}
      </div>

      {users._scope === 'user' && (
        <p className="mb-3 text-xs text-yellow-400/70">Showing current user only. Admin scopes required for full user list.</p>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="pb-2 pr-4">Name</th>
              <th className="pb-2 pr-4">Email</th>
              <th className="pb-2 pr-4">Type</th>
              <th className="pb-2 pr-4">Role</th>
              <th className="pb-2 pr-4">Status</th>
              <th className="pb-2">Last Login</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {displayed.map((user, i) => (
              <tr key={i} className="hover:bg-secondary/30">
                <td className="py-2 pr-4 text-foreground">{user.name}</td>
                <td className="py-2 pr-4 text-muted-foreground">{user.email}</td>
                <td className="py-2 pr-4"><Badge variant="blue">{user.typeName}</Badge></td>
                <td className="py-2 pr-4 text-muted-foreground">{user.role || '—'}</td>
                <td className="py-2 pr-4">
                  <Badge variant={user.status === 'active' ? 'green' : 'default'}>{user.status || '—'}</Badge>
                </td>
                <td className="py-2 text-xs text-muted-foreground">
                  {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {users.users.length > 10 && (
        <button onClick={() => setShowAll(!showAll)} className="mt-3 text-sm text-primary hover:underline">
          {showAll ? 'Show less' : `Show all ${users.users.length} users`}
        </button>
      )}
    </SectionCard>
  );
}

// =============================================================================
// Phone Section
// =============================================================================

function PhoneSection({ phone }: { phone: PhoneData }) {
  return (
    <SectionCard
      title="Zoom Phone"
      icon={<Phone className="h-5 w-5" />}
      badge={<Badge variant={phone._status === 'provisioned' ? 'green' : 'default'}>{phone._status === 'provisioned' ? 'Active' : 'Not Active'}</Badge>}
    >
      {phone._status === 'not_provisioned' ? (
        <NotProvisionedBanner product="Zoom Phone" />
      ) : (
        <>
          {phone.companyName && <SettingRow label="Company" value={phone.companyName} />}
          <SettingRow label="Phone Users" value={phone.totalUsers ?? 0} />
          <SettingRow label="Auto Call Recording" value={phone.autoCallRecording} />
          <SettingRow label="E911 Emergency Service" value={phone.emergencyServiceEnabled} />
          <SettingRow label="Voicemail" value={phone.voicemailEnabled} />
        </>
      )}
    </SectionCard>
  );
}

// =============================================================================
// Rooms Section
// =============================================================================

function RoomsSection({ rooms }: { rooms: RoomsData }) {
  return (
    <SectionCard
      title="Zoom Rooms"
      icon={<Building2 className="h-5 w-5" />}
      badge={<Badge variant={rooms._status === 'provisioned' ? 'green' : 'default'}>{rooms._status === 'provisioned' ? `${rooms.totalRooms ?? 0} rooms` : 'Not Active'}</Badge>}
    >
      {rooms._status === 'not_provisioned' ? (
        <NotProvisionedBanner product="Zoom Rooms" />
      ) : rooms.rooms && rooms.rooms.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="pb-2 pr-4">Name</th>
                <th className="pb-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {rooms.rooms.map((room) => (
                <tr key={room.id} className="hover:bg-secondary/30">
                  <td className="py-2 pr-4 text-foreground">{room.name}</td>
                  <td className="py-2"><Badge variant={room.status === 'Available' ? 'green' : 'yellow'}>{room.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No rooms configured</p>
      )}
    </SectionCard>
  );
}

// =============================================================================
// Client Satisfaction Card
// =============================================================================

function SatisfactionCard({ data }: { data: SatisfactionData }) {
  const total = data.goodCount + data.notGoodCount;

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <span className="text-muted-foreground text-lg">&#9733;</span>
        <h3 className="flex-1 text-sm font-semibold text-foreground">Client Satisfaction</h3>
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-500/10 text-green-500">
          {data._scope}
        </span>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-4 mb-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-foreground">{data.goodPercent}%</p>
            <p className="text-xs text-muted-foreground">Satisfaction</p>
          </div>
          <div className="flex-1">
            <div className="h-3 rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full rounded-full bg-green-500 transition-all"
                style={{ width: `${data.goodPercent}%` }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-green-500">{data.goodCount} good</span>
              <span className="text-[10px] text-red-500">{data.notGoodCount} not good</span>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {total} responses · {data.dateRange.from} to {data.dateRange.to}
        </p>
      </div>
    </div>
  );
}

function TelephoneCard({ data }: { data: TelephoneData }) {
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <PhoneCall className="h-5 w-5 text-muted-foreground" />
        <h3 className="flex-1 text-sm font-semibold text-foreground">Telephone Usage</h3>
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-500/10 text-green-500">
          {data._scope}
        </span>
      </div>
      <div className="p-5 space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-2xl font-bold text-foreground">{data.totalCalls.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Total calls</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">{data.totalMinutes.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Total minutes</p>
          </div>
        </div>
        {Object.keys(data.byType).length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border">
            {Object.entries(data.byType).map(([type, count]) => (
              <span key={type} className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-secondary text-muted-foreground">
                {type}: {count}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RoomMetricsCard({ data }: { data: RoomMetricsData }) {
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <Monitor className="h-5 w-5 text-muted-foreground" />
        <h3 className="flex-1 text-sm font-semibold text-foreground">Room Metrics</h3>
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-500/10 text-green-500">
          {data._scope}
        </span>
      </div>
      <div className="p-5 space-y-3">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-2xl font-bold text-foreground">{data.totalRooms}</p>
            <p className="text-xs text-muted-foreground">Total rooms</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-500">{data.online}</p>
            <p className="text-xs text-muted-foreground">Online</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-muted-foreground">{data.offline}</p>
            <p className="text-xs text-muted-foreground">Offline</p>
          </div>
        </div>
        {data.issues.length > 0 && (
          <div className="space-y-1.5 pt-2 border-t border-border">
            <p className="text-xs font-medium text-orange-500">{data.issues.length} issue{data.issues.length !== 1 ? 's' : ''}</p>
            {data.issues.slice(0, 5).map((issue, i) => (
              <p key={i} className="text-xs text-muted-foreground">
                {issue.roomName}: {issue.issue}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Integrations Section
// =============================================================================

function IntegrationsSection({ data }: { data: ZoomOverviewData }) {
  const { roles, groups, marketplace, archive } = data;

  return (
    <SectionCard title="Integrations & Administration" icon={<Grid3X3 className="h-5 w-5" />}>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Roles */}
        {roles && (
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Roles</p>
          {isNotAvailable(roles) ? (
            <NotAvailableBanner message={roles.message} />
          ) : (
            <div className="space-y-1.5">
              {(roles as RolesData).roles.length === 0
                ? <p className="text-sm text-muted-foreground">No roles configured</p>
                : (roles as RolesData).roles.map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-1">
                      <span className="text-sm text-foreground/80">{r.name}</span>
                      <span className="text-xs text-muted-foreground">{r.totalMembers} members</span>
                    </div>
                  ))
              }
            </div>
          )}
        </div>
        )}

        {/* Groups */}
        {groups && (
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Groups</p>
          {isNotAvailable(groups) ? (
            <NotAvailableBanner message={groups.message} />
          ) : (
            <div className="space-y-1.5">
              {(groups as GroupsData).groups.length === 0
                ? <p className="text-sm text-muted-foreground">No groups configured</p>
                : (groups as GroupsData).groups.map((g) => (
                    <div key={g.id} className="flex items-center justify-between py-1">
                      <span className="text-sm text-foreground/80">{g.name}</span>
                      <span className="text-xs text-muted-foreground">{g.totalMembers} members</span>
                    </div>
                  ))
              }
            </div>
          )}
        </div>
        )}

        {/* Marketplace */}
        {marketplace && (
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Marketplace Apps</p>
          {isNotAvailable(marketplace) ? (
            <NotAvailableBanner message={marketplace.message} />
          ) : (
            <div className="space-y-1.5">
              {(marketplace as MarketplaceData).totalApps === 0
                ? <p className="text-sm text-muted-foreground">No marketplace apps installed</p>
                : (marketplace as MarketplaceData).apps.slice(0, 10).map((a, i) => (
                    <div key={i} className="flex items-center justify-between py-1">
                      <span className="text-sm text-foreground/80">{a.name}</span>
                      {a.status && <Badge>{a.status}</Badge>}
                    </div>
                  ))
              }
              {!isNotAvailable(marketplace) && (marketplace as MarketplaceData).totalApps > 10 && (
                <p className="text-xs text-muted-foreground">+{(marketplace as MarketplaceData).totalApps - 10} more</p>
              )}
            </div>
          )}
        </div>
        )}

        {/* Archive */}
        {archive && (
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Archiving</p>
          {(archive as ArchiveData)._status === 'not_provisioned' ? (
            <NotProvisionedBanner product="Archiving" />
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between py-1">
                <span className="text-sm text-foreground/80">Status</span>
                <Badge variant="green">Active</Badge>
              </div>
              {(archive as ArchiveData).totalFiles !== undefined && (
                <div className="flex items-center justify-between py-1">
                  <span className="text-sm text-foreground/80">Total Files</span>
                  <span className="text-sm text-foreground">{(archive as ArchiveData).totalFiles}</span>
                </div>
              )}
            </div>
          )}
        </div>
        )}
      </div>
    </SectionCard>
  );
}
